export interface CompanyFormValues {
  name: string;
  slug: string;
  industry: string;
  employeeCount: string;
  location: string;
  website: string;
}

export interface CompanyFormStatus {
  normalizedSlug: string;
  isValid: boolean;
}

export type CompanyFormSection = 'identity' | 'details' | 'story' | 'all';

export interface CompanyFormSectionStatus {
  identity: boolean;
  details: boolean;
  story: boolean;
}

export const COMPANY_INDUSTRY_OPTIONS = [
  'Technology',
  'Financial Services',
  'Healthcare',
  'Education',
  'Government',
  'Retail & E-commerce',
  'Hospitality & Tourism',
  'Construction & Real Estate',
  'Logistics & Transportation',
  'Professional Services',
  'Media & Marketing',
  'Other',
] as const;

export const COMPANY_SIZE_OPTIONS = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1,000',
  '1,001-5,000',
  '5,000+',
] as const;

export function toCompanySlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

export function ensureCompanyWebsite(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function getCompanyFormStatus(
  values: CompanyFormValues,
): CompanyFormStatus {
  const normalizedSlug = toCompanySlug(values.slug || values.name);

  const sections = getCompanyFormSectionStatus(values);

  return {
    normalizedSlug,
    isValid: sections.identity && sections.details && sections.story,
  };
}

export function getCompanyFormSectionStatus(
  values: CompanyFormValues,
): CompanyFormSectionStatus {
  const normalizedSlug = toCompanySlug(values.slug || values.name);

  return {
    identity: values.name.trim().length > 1 && normalizedSlug.length >= 3,
    details:
      values.industry.trim().length > 0 &&
      values.employeeCount.trim().length > 0 &&
      values.location.trim().length > 0,
    // Website and logo are both optional, so the story step has nothing left to
    // gate on — kept as a key so the three-step wizard still tracks per-step state.
    story: true,
  };
}
